import React from 'react';
import { useTranslation } from 'react-i18next';
import { BotMessageSquare, PanelLeft } from 'lucide-react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { cn } from '@/lib/utils';
import { LAYOUT, Z_INDEX } from '@constants/layout';
import UserMenu from './UserMenu';

interface HeaderProps {
  chatOpen?: boolean;
  /** The chat toggle hides when chat is unavailable — e.g. the CAD thickness view. */
  chatAvailable?: boolean;
  onToggleNav?: () => void;
  onToggleChat?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  chatOpen = false,
  chatAvailable = true,
  onToggleNav,
  onToggleChat,
}) => {
  const { t } = useTranslation();

  return (
    <TooltipProvider>
      <header
        style={{ height: LAYOUT.HEADER_HEIGHT, zIndex: Z_INDEX.HEADER }}
        className={cn(
          'flex items-center gap-2 border-b px-3 sm:px-4',
          'bg-card/85 supports-[backdrop-filter]:bg-card/70 supports-[backdrop-filter]:backdrop-blur-xl',
        )}
      >
        {onToggleNav && (
          <TooltipIconButton
            label={t('navigation.toggleSidebar')}
            icon={<PanelLeft strokeWidth={1.75} />}
            onClick={onToggleNav}
          />
        )}

        <div className="ml-auto flex items-center gap-1">
          {chatAvailable && (
            <TooltipIconButton
              label={t('navigation.toggleChat')}
              // Says what it opens — an AI conversation — where Sparkles only said
              // "AI something". It also stops colliding with the assistant's own
              // sparkle marks elsewhere in the UI.
              icon={<BotMessageSquare strokeWidth={1.75} />}
              aria-pressed={chatOpen}
              onClick={onToggleChat}
              className={cn(
                'transition-colors [&_svg]:transition-transform [&_svg]:duration-200 [&_svg]:ease-shell',
                // Leans toward the panel it pulls in, and back when it closes.
                chatOpen && 'bg-accent text-primary [&_svg]:-translate-x-px [&_svg]:scale-110',
                'motion-reduce:[&_svg]:transition-none',
              )}
            />
          )}
          <UserMenu />
        </div>
      </header>
    </TooltipProvider>
  );
};

export default Header;
